import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import * as _ from 'lodash-es';
import { finalize, merge, Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  extractObject,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ProjectTaggingService } from './project-tagging.service';

@Component({
    selector: 'gtw-project-tagging',
    templateUrl: './project-tagging.component.html',
    styleUrls: ['./project-tagging.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectTaggingComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly destroy$ = new Subject<void>();
  private isInitialized = false;
  private isLoading = false;
  private projectsRequestId = 0;
  private stepsRequestId = 0;

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  modalTitle = 'Project Tag';
  entities: any[] = [];
  projectList: any[] = [];
  projectStepList: any[] = [];
  project: any = null;
  projectStep: any = null;
  projectLoading = true;
  projectStepLoading = false;
  hideProjectSteps = false;
  formSubmitted = false;

  oldProjectId: any = null;
  oldProjectStepId: any = null;

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  gridData: any = {};
  filterParams: any = {};

  constructor(
    private projectTaggingService: ProjectTaggingService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    merge(this.gtwWorkspace.setTabActive, this.gtwGlobal.globalParamsChanged$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFromWorkspace());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncFromWorkspace(): void {
    this.applyWorkspaceContext();
    this.isInitialized = false;
    this.isLoading = false;
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = shell.generalConfigData || {};
    this.GENERAL_CONFIG = {
      ...gc,
      base_url: gc.base_url ?? gc.api ?? ''
    };
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};

    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.globalService);
    }
    this.filterParams = fp;

    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    let rowObj: any = {};
    if (Array.isArray(rawRow) && rawRow.length > 0) {
      const first = rawRow[0] as any;
      rowObj = first?.data != null ? extractObject(first.data) : extractObject(first);
    } else {
      rowObj = extractObject(rawRow);
    }
    this.rowData = rowObj;

    const sel = activeScreen?.data?.selected;
    const g = gridPayloadFromActiveScreen(activeScreen);
    const rp = rowPayloadFromActiveScreen(activeScreen);
    let nextGrid: any = sel != null ? sel : g;
    if (
      nextGrid == null ||
      (typeof nextGrid === 'object' &&
        !Array.isArray(nextGrid) &&
        Object.keys(nextGrid as object).length === 0)
    ) {
      if (Array.isArray(rp) && rp.length > 0) {
        nextGrid = rp;
      } else if (rp && typeof rp === 'object' && !Array.isArray(rp) && Object.keys(rp).length > 0) {
        const raw = rp as any;
        nextGrid = raw?.data != null ? raw : { rowData: raw };
      }
    }
    const hasGridPayload =
      nextGrid != null &&
      (Array.isArray(nextGrid) ? nextGrid.length > 0 : typeof nextGrid === 'object' && Object.keys(nextGrid as object).length > 0);
    this.gridData = hasGridPayload ? nextGrid : {};
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.GENERAL_CONFIG?.base_url || !this.gridData || Object.keys(this.gridData).length === 0) {
      return;
    }

    this.isLoading = true;
    this.isInitialized = true;
    try {
      this.init();
    } catch {
      this.isInitialized = false;
      this.isLoading = false;
    }
  }

  private init(): void {
    this.normalizeGridData();
    this.setProjectFromRows();
    this.loadProjects();
  }

  private normalizeGridData(): void {
    if (!Array.isArray(this.gridData)) {
      const rowObject = this.gridData?.rowData || this.gridData || {};
      this.entities = [{ row_id: rowObject.object_id, data: rowObject }];
      return;
    }

    this.entities = _.cloneDeep(this.gridData);
  }

  private setProjectFromRows(): void {
    try {
      const projectIds: any[] = [];
      _.forEach(this.entities, (value: any) => {
        const projectId = value?.data?.PROJECT_ID;
        if (!_.includes(projectIds, projectId)) {
          projectIds.push(projectId);
        }
      });
      if (projectIds.length === 1) {
        this.oldProjectId = { PROJECT_ID: projectIds[0] };
      }
    } catch (e) {
      console.info(e);
    }
  }

  private loadProjects(): void {
    const reqId = ++this.projectsRequestId;
    this.showFormLoader = true;
    this.projectLoading = true;

    this.projectTaggingService
      .loadProjects(this.GENERAL_CONFIG.base_url, this.globalService, this.filterParams, this.rowData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (data: any) => {
          if (reqId !== this.projectsRequestId) {
            return;
          }
          if (data?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Unable to load projects', 'gtw-snackbar--red');
          } else {
            this.projectList = data?.jsonObject || [];
            this.projectList.unshift({ PROJECT_ID: null, PROJECT_NAME: 'Remove Project Tag' });
            if (this.oldProjectId) {
              this.project = _.find(this.projectList, { PROJECT_ID: this.oldProjectId.PROJECT_ID }) || null;
              this.oldProjectId = this.project;
            }
            this.loadProjectSteps();
          }
          this.projectLoading = false;
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        () => {
          if (reqId !== this.projectsRequestId) {
            return;
          }
          this.projectLoading = false;
          this.showFormLoader = false;
          this.gtwSnackbarService.openSnackBar('Error loading project data', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  loadProjectSteps(): void {
    if (!this.project || !this.project.PROJECT_ID) {
      this.hideProjectSteps = true;
      this.projectStep = null;
      this.projectStepList = [];
      this.cdr.detectChanges();
      return;
    }

    const reqId = ++this.stepsRequestId;
    this.hideProjectSteps = false;
    this.projectStepLoading = true;

    this.projectTaggingService
      .loadProjectSteps(this.GENERAL_CONFIG.base_url, this.project.PROJECT_ID, this.globalService, this.filterParams, this.rowData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          if (reqId !== this.stepsRequestId) {
            return;
          }
          if (data?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Unable to load project steps', 'gtw-snackbar--red');
            this.projectStepLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.projectStepList = data?.jsonObject || [];
          this.projectStepLoading = false;

          if (this.projectStepList.length === 0) {
            this.hideProjectSteps = true;
            this.projectStep = null;
          } else {
            this.hideProjectSteps = false;
            this.setProjectStepFromRows();
          }
          this.cdr.detectChanges();
        },
        () => {
          if (reqId !== this.stepsRequestId) {
            return;
          }
          this.projectStepLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading project steps', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  private setProjectStepFromRows(): void {
    try {
      const projectStepArray: any[] = [];
      _.forEach(this.entities, (value: any) => {
        const stepId = value?.data?.PROJECT_STEP_ID;
        if (!_.includes(projectStepArray, stepId)) {
          projectStepArray.push(stepId);
        }
      });

      if (projectStepArray.length === 1) {
        const projectStepId = parseInt(projectStepArray[0], 10);
        this.projectStep = _.find(this.projectStepList, { PROJECT_STEP_ID: projectStepId }) || null;
        this.oldProjectStepId = this.projectStep;
      }
    } catch (e) {
      console.info(e);
    }
  }

  removeEntity(index: number): void {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.cancel();
    }
    this.cdr.detectChanges();
  }

  save(): void {
    this.formSubmitted = true;
    if (!this.project) {
      this.gtwSnackbarService.openSnackBar('Please select a project', 'gtw-snackbar--yellow');
      return;
    }

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const sendObj = this.buildSavePayload();
    this.projectTaggingService
      .saveProjectTag(this.GENERAL_CONFIG.base_url, sendObj, this.globalService, this.filterParams, this.rowData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (result?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error while saving project tag', 'gtw-snackbar--red');
          } else if (this.project?.PROJECT_ID == null) {
            this.gtwSnackbarService.openSnackBar('Project was successfully untagged', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar('Project was successfully tagged', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  private buildSavePayload(): any[] {
    const sendObj: any[] = [];

    (this.entities || []).forEach((row: any) => {
      const dataObj = row?.data || {};
      const projectTag: any = {
        COMBINATION_KEY: dataObj.COMBINATION_KEY,
        LEID: dataObj.LEID,
        CDR_NO: dataObj.CDR_NO || dataObj.HO_CDR_NO,
        TAX_YEAR: dataObj.TAX_YEAR ?? this.rowData?.TAX_YEAR,
        SCENARIO: this.rowData?.SCENARIO ?? this.filterParams?.scenario ?? this.globalService?.scenario,
        JCD_KEY: this.rowData?.JCD_KEY ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key,
        TRANSHEADER_KEY: dataObj.TRANS_HEADER_KEY,
        TRANSDETAILS_KEY: dataObj.TRANS_DETAILS_KEY,
        Acct_key: dataObj.ACCT_KEY
      };

      if (this.oldProjectId != null) {
        projectTag.OLD_PROJECT_ID = this.oldProjectId.PROJECT_ID;
      }
      if (this.project != null) {
        projectTag.NEW_PROJECT_ID = this.project.PROJECT_ID;
      }
      if (this.oldProjectStepId != null) {
        projectTag.OLD_PROJECT_STEP_ID = this.oldProjectStepId.PROJECT_STEP_ID;
      }
      if (this.projectStep != null) {
        projectTag.NEW_PROJECT_STEP_ID = this.projectStep.PROJECT_STEP_ID;
      }

      sendObj.push(projectTag);
    });

    return sendObj;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
